import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios";
import { useRole } from "../context/RoleContext";
import { progressReportTradeLabel } from "../utils/progressReportUtils";
import { isAgentRecordLocked, isTradeFinalized } from "../utils/appAuth";
import { documentCategoryForTradePdf } from "../utils/tradePdfDocuments";
import RequestMissingDocumentsModal from "./RequestMissingDocumentsModal";
import PreConstructionUploadsModal from "./PreConstructionUploadsModal";

const canPreviewFile = (file) => {
  const mime = String(file?.mimeType || "").toLowerCase();
  const name = String(file?.originalName || "").toLowerCase();
  return (
    mime.startsWith("image/") ||
    mime === "application/pdf" ||
    /\.(pdf|png|jpe?g|webp)$/.test(name)
  );
};

function isPreConstructionTrade(trade) {
  const k = trade?.keyInfo || {};
  const installments = Number(k.preConstructionInstallments);
  if (installments === 2 || installments === 3) return true;
  if (k.preConstructionUploadNumber) return true;
  const tn = String(trade?.tradeNumber || k.tradeNumber || "").trim();
  return /^\d+-[ABC]$/i.test(tn);
}

function suggestedCategoryForTrade(trade) {
  if (isPreConstructionTrade(trade)) return "pre-construction";
  return documentCategoryForTradePdf(
    trade?.keyInfo?.dealType || trade?.keyInfo?.propertyType || "",
    trade?.keyInfo?.classification || ""
  );
}

function rememberedCategory(record) {
  if (isPreConstructionTrade(record)) return "pre-construction";
  const saved = String(record?.lastDocumentCategory || "").trim();
  if (saved) return saved;
  const packs = Array.isArray(record?.documentPacks) ? record.documentPacks : [];
  let best = "";
  let bestTime = 0;
  for (const pack of packs) {
    for (const file of pack.files || []) {
      const t = file.uploadedAt ? new Date(file.uploadedAt).getTime() : 0;
      if (pack.category && t >= bestTime) {
        bestTime = t;
        best = pack.category;
      }
    }
  }
  if (best) return best;
  const withFiles = packs.find((pack) => (pack.files || []).length);
  return withFiles?.category || suggestedCategoryForTrade(record);
}

/** Prefer listing/cooperating category from trade classification when set. */
function preferredDocumentCategory(record) {
  if (isPreConstructionTrade(record)) return "pre-construction";
  const suggested = suggestedCategoryForTrade(record);
  const classification = String(record?.keyInfo?.classification || "").trim();
  if (classification && suggested) return suggested;
  return rememberedCategory(record) || suggested;
}

const TradeDocumentsSection = ({ trades }) => {
  const { isAgent, isSuperUser, isAdmin, isBroker } = useRole();
  const [searchParams] = useSearchParams();
  const canManageFiles = isAgent || isSuperUser || isAdmin || isBroker;
  /** Agents, Super Users, and Admins can create/upload 7000+ pre-construction packs. */
  const canUploadPreConstruction = isAgent || isSuperUser || isAdmin;
  const canRequestMissing = isSuperUser || isAdmin;
  const [categories, setCategories] = useState([]);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [columns, setColumns] = useState([]);
  const [savedFiles, setSavedFiles] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestItemIds, setRequestItemIds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [openingId, setOpeningId] = useState("");
  const [removingId, setRemovingId] = useState("");
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [lockedForAgent, setLockedForAgent] = useState(false);
  const [lockedAt, setLockedAt] = useState(null);
  const [tradeFinalized, setTradeFinalized] = useState(false);
  const [revisedModalOpen, setRevisedModalOpen] = useState(false);
  const [revisedDocument, setRevisedDocument] = useState(null);
  const [revisedUploading, setRevisedUploading] = useState(false);
  const [preConstructionUploadsOpen, setPreConstructionUploadsOpen] =
    useState(false);
  const [trsSignatures, setTrsSignatures] = useState(null);
  const [trsLoading, setTrsLoading] = useState(false);
  const [trsActionId, setTrsActionId] = useState("");
  const fileInputRef = useRef(null);
  const preConFileInputRef = useRef(null);
  const revisedFileInputRef = useRef(null);
  const previewUrlRef = useRef(null);
  const documentsLocked =
    isAgent &&
    (lockedForAgent || isAgentRecordLocked(selectedTrade?.trade, "agent"));
  const tradeIsFinalized =
    tradeFinalized || isTradeFinalized(selectedTrade?.trade);

  const applyLockState = (data) => {
    if (!data || typeof data !== "object") return;
    if (typeof data.lockedForAgent === "boolean") {
      setLockedForAgent(data.lockedForAgent);
    }
    if (data.lockedAt) setLockedAt(data.lockedAt);
    if (typeof data.isFinalized === "boolean") {
      setTradeFinalized(data.isFinalized);
    }
  };

  const isAgreementUpload = (file) => {
    const id = String(file?.itemId || file?.id || "")
      .toLowerCase()
      .replace(/_/g, "-");
    return (
      id === "agreement-purchase-sale" ||
      id === "agreement-to-lease" ||
      id.startsWith("agreement-purchase-sale") ||
      id.startsWith("agreement-to-lease")
    );
  };

  const canRemoveFile = (file) => {
    if (isBroker) return false;
    if (file?.pending) return canManageFiles;
    if (!canManageFiles) return false;
    if (!isAgent) return true;
    if (tradeIsFinalized) return false;
    // Agents can delete/replace the uploaded APS / lease agreement.
    if (isAgreementUpload(file)) return true;
    if (!documentsLocked) return true;
    if (!lockedAt || !file?.uploadedAt) return false;
    return new Date(file.uploadedAt).getTime() > new Date(lockedAt).getTime();
  };

  const tradeOptions = useMemo(
    () =>
      (trades || []).map((trade) => ({
        value: trade._id,
        label: progressReportTradeLabel(trade),
        trade,
      })),
    [trades]
  );

  const isSelectedPreConstruction = isPreConstructionTrade(
    selectedTrade?.trade,
  );

  const categoryOptions = useMemo(() => {
    const all = categories.map((c) => ({ value: c.id, label: c.label }));
    if (isSelectedPreConstruction) {
      return all.filter((c) => c.value === "pre-construction");
    }
    return all.filter((c) => c.value !== "pre-construction");
  }, [categories, isSelectedPreConstruction]);

  useEffect(() => {
    const tradeId = searchParams.get("trade");
    const categoryId = searchParams.get("category");
    if (tradeId && tradeOptions.length) {
      const match = tradeOptions.find(
        (option) => String(option.value) === String(tradeId)
      );
      if (match) setSelectedTrade(match);
    }
    if (categoryId && categoryOptions.length) {
      const match = categoryOptions.find((option) => option.value === categoryId);
      if (match) setSelectedCategory(match);
    }
  }, [searchParams, tradeOptions, categoryOptions]);

  useEffect(() => {
    const urlCategory = searchParams.get("category");
    const urlTrade = searchParams.get("trade");
    if (
      urlCategory &&
      urlTrade &&
      String(urlTrade) === String(selectedTrade?.value)
    ) {
      return;
    }
    if (!selectedTrade) {
      setSelectedCategory(null);
      return;
    }
    if (!categoryOptions.length) return;
    let cancelled = false;
    const applyCategory = (categoryId) => {
      const match = categoryId
        ? categoryOptions.find((option) => option.value === categoryId)
        : null;
      if (!cancelled) setSelectedCategory(match || null);
    };
    if (isPreConstructionTrade(selectedTrade.trade)) {
      applyCategory("pre-construction");
      if (selectedTrade.trade) {
        selectedTrade.trade.lastDocumentCategory = "pre-construction";
      }
      axiosInstance
        .patch(`/trade-documents/${selectedTrade.value}/category`, {
          category: "pre-construction",
        })
        .catch(() => {});
      return () => {
        cancelled = true;
      };
    }
    const localSaved = preferredDocumentCategory(selectedTrade.trade);
    applyCategory(localSaved);
    axiosInstance
      .get(`/trade-documents/${selectedTrade.value}`)
      .then((res) => {
        const suggested =
          res.data.suggestedCategory ||
          suggestedCategoryForTrade(selectedTrade.trade);
        const classification = String(
          selectedTrade.trade?.keyInfo?.classification || ""
        ).trim();
        const saved = classification
          ? suggested ||
            res.data.lastDocumentCategory ||
            localSaved
          : res.data.lastDocumentCategory || suggested || localSaved;
        if (selectedTrade?.trade && saved) {
          selectedTrade.trade.lastDocumentCategory = saved;
        }
        applyCategory(saved);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTrade?.value, categoryOptions]);

  useEffect(() => {
    setRequestModalOpen(false);
    setRequestItemIds([]);
    setSelectedDocument(null);
    setRevisedModalOpen(false);
    setRevisedDocument(null);
    setLockedForAgent(false);
    setLockedAt(null);
    setTradeFinalized(false);
  }, [selectedTrade?.value, selectedCategory?.value]);

  useEffect(() => {
    if (!selectedTrade?.value) {
      setTrsSignatures(null);
    }
  }, [selectedTrade?.value]);

  useEffect(() => {
    if (!selectedTrade?.value || isAgent) {
      setTrsSignatures(null);
      return;
    }
    let cancelled = false;
    const loadTrs = async () => {
      try {
        setTrsLoading(true);
        const res = await axiosInstance.get(
          `/trades/${selectedTrade.value}/trs-signature`
        );
        if (!cancelled) setTrsSignatures(res.data.trsSignatures || {});
      } catch (_) {
        if (!cancelled) setTrsSignatures({});
      } finally {
        if (!cancelled) setTrsLoading(false);
      }
    };
    loadTrs();
    return () => {
      cancelled = true;
    };
  }, [selectedTrade?.value, isAgent]);

  const displayedFiles = useMemo(
    () => [...savedFiles, ...pendingFiles],
    [savedFiles, pendingFiles]
  );

  const closePreview = () => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreview(null);
  };

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        window.URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const fetchFileBlob = async (file, view) => {
    const res = await axiosInstance.get(
      `/trade-documents/${selectedTrade.value}/files/${file.fileId}`,
      {
        params: { category: selectedCategory.value, view: view ? "1" : undefined },
        responseType: "blob",
        timeout: 120000,
      }
    );
    const mime =
      file.mimeType ||
      res.headers["content-type"] ||
      "application/octet-stream";
    return new Blob([res.data], { type: mime });
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axiosInstance.get("/trade-documents/categories");
        setCategories(res.data.categories || []);
      } catch (err) {
        toast.error(
          err.response?.data?.message || "Could not load document categories."
        );
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadPack = async () => {
      if (!selectedTrade || !selectedCategory) {
        setColumns([]);
        setSavedFiles([]);
        setPendingFiles([]);
        return;
      }
      setLoading(true);
      try {
        const preCon = isPreConstructionTrade(selectedTrade.trade);
        const uploadNumber =
          selectedTrade.trade?.keyInfo?.preConstructionUploadNumber;
        if (
          preCon &&
          uploadNumber &&
          selectedCategory.value === "pre-construction"
        ) {
          try {
            await axiosInstance.post(
              `/pre-construction-uploads/by-number/${uploadNumber}/attach`,
              { tradeId: selectedTrade.value },
            );
          } catch (_) {
            /* attach is best-effort; pack load below still runs */
          }
        }
        const res = await axiosInstance.get(
          `/trade-documents/${selectedTrade.value}`,
          { params: { category: selectedCategory.value } }
        );
        setColumns(preCon ? [] : res.data.columns || []);
        setSavedFiles(res.data.files || []);
        setPendingFiles([]);
        applyLockState(res.data);
      } catch (err) {
        setColumns([]);
        setSavedFiles([]);
        setPendingFiles([]);
        toast.error(
          err.response?.data?.message || "Could not load documents for this trade."
        );
      } finally {
        setLoading(false);
      }
    };
    loadPack();
  }, [selectedTrade, selectedCategory]);

  const updateItem = (updated) => {
    setColumns((prev) =>
      prev.map((col) => col.map((item) => (item.id === updated.id ? updated : item)))
    );
  };

  const persistFiles = async (files, options = {}) => {
    const itemId = options.itemId || selectedDocument?.value;
    if (!itemId) {
      throw new Error("Select the document you are uploading.");
    }
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    formData.append("category", selectedCategory.value);
    formData.append("itemId", itemId);
    if (options.isRevision) {
      formData.append("isRevision", "true");
    }
    const res = await axiosInstance.post(
      `/trade-documents/${selectedTrade.value}/files`,
      formData,
      { timeout: 180000 }
    );
    setSavedFiles(res.data.files || []);
    setColumns((prev) => {
      const source = res.data.columns || prev;
      return source.map((col) =>
        col.map((item) =>
          item.id === itemId ? { ...item, checked: true } : item
        )
      );
    });
    applyLockState(res.data);
    if (selectedTrade?.trade && selectedCategory?.value) {
      selectedTrade.trade.lastDocumentCategory = selectedCategory.value;
    }
    if (Array.isArray(selectedTrade?.trade?.documentPacks) && selectedCategory?.value) {
      const packs = selectedTrade.trade.documentPacks;
      if (!packs.some((pack) => pack.category === selectedCategory.value)) {
        packs.push({
          category: selectedCategory.value,
          items: [],
          files: res.data.files || [],
        });
      }
    }
  };

  const handleUpload = async (selectedFiles) => {
    if (!selectedFiles?.length || !selectedTrade || !selectedCategory) return;
    if (!selectedDocument?.value) {
      toast.error("Select the document you are uploading first.");
      return;
    }
    try {
      setSaving(true);
      await persistFiles(selectedFiles);
      setSelectedDocument(null);
      toast.success(
        selectedFiles.length === 1
          ? "Document saved. Compliance review will run automatically and email the agent."
          : "Documents saved. Compliance review will run automatically and email the agent.",
      );
    } catch (err) {
      const message =
        err.response?.data?.message ||
        "Could not save files. Click Save to try again.";
      if (err.response?.data?.code !== "WRONG_FORM") {
        const next = selectedFiles.map((file, index) => ({
          fileId: `pending-${Date.now()}-${index}-${file.name}`,
          originalName: file.name,
          mimeType: file.type,
          pending: true,
          file,
        }));
        setPendingFiles((prev) => [...prev, ...next]);
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  /** Upload into the linked 7000+ pack for a pre-construction trade. */
  const handlePreConstructionTradeUpload = async (selectedFiles) => {
    if (!selectedFiles?.length || !selectedTrade?.value) return;
    if (!canUploadPreConstruction) {
      toast.error("You do not have permission to upload pre-construction documents.");
      return;
    }
    const uploadNumber =
      selectedTrade.trade?.keyInfo?.preConstructionUploadNumber;
    if (!uploadNumber) {
      toast.error(
        "Link a Pre Construction Upload number on Trade Info first, or create one under Pre Construction Uploads.",
      );
      setPreConstructionUploadsOpen(true);
      return;
    }
    try {
      setSaving(true);
      const meta = await axiosInstance.get(
        `/pre-construction-uploads/by-number/${uploadNumber}`,
      );
      const packId = meta.data?._id;
      if (!packId) {
        throw new Error("Upload number not found.");
      }
      const postFiles = async (files) => {
        const formData = new FormData();
        for (const file of files) formData.append("files", file);
        await axiosInstance.post(
          `/pre-construction-uploads/${packId}/files`,
          formData,
          {
            timeout: 180000,
            headers: { "Content-Type": undefined },
          },
        );
      };
      try {
        await postFiles(selectedFiles);
      } catch (batchErr) {
        if (selectedFiles.length <= 1) throw batchErr;
        let uploaded = 0;
        for (const file of selectedFiles) {
          try {
            await postFiles([file]);
            uploaded += 1;
          } catch (oneErr) {
            console.error("pre-con file upload failed:", file.name, oneErr);
          }
        }
        if (!uploaded) throw batchErr;
        if (uploaded < selectedFiles.length) {
          toast.warn(
            `Uploaded ${uploaded} of ${selectedFiles.length} files. Some could not be saved.`,
          );
        }
      }
      await axiosInstance.post(
        `/pre-construction-uploads/by-number/${uploadNumber}/attach`,
        { tradeId: selectedTrade.value },
      );
      const res = await axiosInstance.get(
        `/trade-documents/${selectedTrade.value}`,
        { params: { category: "pre-construction" } },
      );
      setColumns([]);
      setSavedFiles(res.data.files || []);
      applyLockState(res.data);
      toast.success(
        selectedFiles.length === 1 ? "Document uploaded." : "Documents uploaded.",
      );
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Could not upload pre-construction documents.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!selectedTrade || !selectedCategory) return;
    if (!pendingFiles.length) {
      toast.success("Documents are already saved.");
      return;
    }
    if (!selectedDocument?.value) {
      toast.error("Select the document you are uploading first.");
      return;
    }
    try {
      setSaving(true);
      await persistFiles(pendingFiles.map((row) => row.file));
      setPendingFiles([]);
      setSelectedDocument(null);
      toast.success(
        "Documents saved. Compliance review will run automatically and email the agent.",
      );
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save files.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (file) => {
    if (file.pending) {
      setPendingFiles((prev) => prev.filter((row) => row.fileId !== file.fileId));
      return;
    }
    if (!selectedTrade || !selectedCategory) return;
    if (!canRemoveFile(file)) {
      toast.error(
        tradeIsFinalized
          ? "This trade has been finalized. You cannot remove uploaded documents."
          : "This trade has been reviewed. You cannot remove documents that were on file at review."
      );
      return;
    }
    try {
      setRemovingId(file.fileId);
      const res = await axiosInstance.delete(
        `/trade-documents/${selectedTrade.value}/files/${file.fileId}`,
        { params: { category: selectedCategory.value } }
      );
      setSavedFiles(res.data.files || []);
      if (res.data.columns) setColumns(res.data.columns);
      applyLockState(res.data);
      toast.success("File removed.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not remove that file.");
    } finally {
      setRemovingId("");
    }
  };

  const handleCheck = async (item, checked) => {
    if (!selectedTrade || !selectedCategory) return;
    try {
      const res = await axiosInstance.patch(
        `/trade-documents/${selectedTrade.value}/items/${item.id}`,
        { category: selectedCategory.value, checked }
      );
      updateItem(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not update checkbox.");
    }
  };

  const handleOtherText = async (item, otherText) => {
    if (!selectedTrade || !selectedCategory) return;
    try {
      const res = await axiosInstance.patch(
        `/trade-documents/${selectedTrade.value}/items/${item.id}`,
        { category: selectedCategory.value, otherText }
      );
      updateItem(res.data);
    } catch (_) {}
  };

  const openPreview = (url, file, mimeType) => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
    }
    previewUrlRef.current = url;
    setPreview({
      url,
      name: file.originalName || "document",
      mimeType: mimeType || file.mimeType || "",
    });
  };

  const handleView = async (file) => {
    if (file.pending && file.file) {
      if (!canPreviewFile(file)) {
        handleDownload(file);
        return;
      }
      openPreview(window.URL.createObjectURL(file.file), file, file.file.type);
      return;
    }
    if (!canPreviewFile(file)) {
      handleDownload(file);
      return;
    }
    try {
      setOpeningId(file.fileId);
      const blob = await fetchFileBlob(file, true);
      openPreview(window.URL.createObjectURL(blob), file, blob.type);
    } catch (err) {
      toast.error("Could not open that file.");
    } finally {
      setOpeningId("");
    }
  };

  const handleDownload = async (file) => {
    try {
      setOpeningId(file.fileId);
      const blob = file.pending && file.file
        ? file.file
        : await fetchFileBlob(file, false);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.originalName || "document";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Could not download that file.");
    } finally {
      setOpeningId("");
    }
  };

  const fetchSignedTrsBlob = async (kind, view) => {
    const res = await axiosInstance.get(
      `/trades/${selectedTrade.value}/trs-signature/${kind}/file`,
      {
        responseType: "blob",
        timeout: 120000,
        params: view ? { view: "1" } : undefined,
      }
    );
    const type = res.headers["content-type"] || "";
    if (type.includes("application/json")) {
      const text = await res.data.text?.();
      let message = "Could not open the signed TRS.";
      try {
        message = JSON.parse(text).message || message;
      } catch (_) {}
      throw new Error(message);
    }
    return new Blob([res.data], { type: "application/pdf" });
  };

  const handleViewSignedTrs = async (kind) => {
    const actionKey = `view-${kind}`;
    try {
      setTrsActionId(actionKey);
      const blob = await fetchSignedTrsBlob(kind, true);
      openPreview(
        window.URL.createObjectURL(blob),
        {
          fileId: `trs-${kind}`,
          originalName: `${kind === "closed" ? "Closed" : "Open"} TRS (signed).pdf`,
        },
        "application/pdf"
      );
    } catch (err) {
      toast.error(err.message || "Could not open the signed TRS.");
    } finally {
      setTrsActionId("");
    }
  };

  const handleDownloadSignedTrs = async (kind) => {
    const actionKey = `download-${kind}`;
    try {
      setTrsActionId(actionKey);
      const blob = await fetchSignedTrsBlob(kind, false);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${kind === "closed" ? "Closed" : "Open"}-TRS-Trade-${
        selectedTrade?.trade?.tradeNumber || selectedTrade?.value
      }-signed.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Could not download the signed TRS.");
    } finally {
      setTrsActionId("");
    }
  };

  const documentOptions = useMemo(
    () =>
      columns
        .flat()
        .filter(Boolean)
        .map((item) => {
          const extra = String(item.otherText || "").trim();
          const label = String(item.label || "").replace(/\.$/, "");
          return {
            value: item.id,
            label: extra ? `${label} (${extra})` : label,
            item,
          };
        }),
    [columns]
  );

  const revisedDocumentOptions = useMemo(
    () =>
      documentOptions.filter(
        (option) => option.value !== "revised-documents"
      ),
    [documentOptions]
  );

  const handleRevisedUpload = async (selectedFiles) => {
    if (!selectedFiles?.length || !selectedTrade || !selectedCategory) return;
    if (!revisedDocument?.value) {
      toast.error("Select the document you are revising first.");
      return;
    }
    try {
      setRevisedUploading(true);
      await persistFiles(selectedFiles, {
        itemId: revisedDocument.value,
        isRevision: true,
      });
      setRevisedModalOpen(false);
      setRevisedDocument(null);
      toast.success(
        selectedFiles.length === 1
          ? "Revised document saved. The previous file is still on file."
          : "Revised documents saved. Previous files are still on file."
      );
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Could not save the revised document."
      );
    } finally {
      setRevisedUploading(false);
    }
  };

  const missingItems = useMemo(
    () =>
      columns
        .flat()
        .filter(
          (item) =>
            item &&
            !item.checked &&
            !(item.isOther && !String(item.otherText || "").trim())
        ),
    [columns]
  );
  const missingCount = missingItems.length;

  const openRequestModal = () => {
    if (!selectedTrade || !selectedCategory) return;
    setRequestItemIds(missingItems.map((item) => item.id));
    setRequestModalOpen(true);
  };

  const closeRequestModal = () => {
    if (sending) return;
    setRequestModalOpen(false);
    setRequestItemIds([]);
  };

  const handleRequestMissing = async (payload = {}) => {
    if (!selectedTrade || !selectedCategory) return;
    const otherMessage = String(payload.otherMessage || "").trim();
    if (!requestItemIds.length && !otherMessage) {
      toast.error("Select at least one document or type an Other message.");
      return;
    }
    setSending(true);
    try {
      const res = await axiosInstance.post(
        `/trade-documents/${selectedTrade.value}/request-missing`,
        {
          category: selectedCategory.value,
          missingItemIds: requestItemIds,
          otherMessage,
        }
      );
      toast.success(res.data.message || "Request sent to the agent.");
      setRequestModalOpen(false);
      setRequestItemIds([]);
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Could not send the missing-documents email."
      );
    } finally {
      setSending(false);
    }
  };

  const handleCategoryChange = (option) => {
    setSelectedCategory(option);
    if (!selectedTrade?.value || !option?.value) return;
    if (selectedTrade.trade) {
      selectedTrade.trade.lastDocumentCategory = option.value;
    }
    axiosInstance
      .patch(`/trade-documents/${selectedTrade.value}/category`, {
        category: option.value,
      })
      .catch(() => {});
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-2">Documents</h2>
      <p className="text-sm text-gray-600 mb-3">
        {isBroker
          ? "Select a trade and category to view uploaded documents. Signed Trade Record Sheets can be viewed or downloaded below when ready."
          : "Select a trade, category, and document, then upload. The selected document is checked off automatically after the file is saved."}
        {canRequestMissing
          ? " Super Users can email the agent to request selected unchecked documents."
          : ""}
        {tradeIsFinalized
          ? " This trade has been finalized, so uploaded documents can no longer be removed."
          : documentsLocked
            ? " This trade has been reviewed. Documents on file at review cannot be removed. Files uploaded after review can be removed until the trade is finalized."
            : ""}
      </p>
      {!isBroker && (
      <p className="text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 mb-4">
        Do not upload the APS again. Once the Agreement of Purchase and Sale is
        uploaded in Trade Info, it is saved here automatically. Upload the other
        listed documents. They are saved automatically. Select the document name
        before you upload so it is ticked off for the office.
      </p>
      )}
      {canUploadPreConstruction ? (
        <div className="mb-6">
          <button
            type="button"
            onClick={() => setPreConstructionUploadsOpen(true)}
            className="px-4 py-2 bg-white text-blue-900 border border-blue-900 rounded-md text-sm hover:bg-blue-50"
          >
            Pre Construction Uploads
          </button>
        </div>
      ) : (
        <div className="mb-6" />
      )}
      <PreConstructionUploadsModal
        open={preConstructionUploadsOpen}
        onClose={() => setPreConstructionUploadsOpen(false)}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Trade <span className="text-red-600">*</span>
          </label>
          <Select
            options={tradeOptions}
            value={selectedTrade}
            onChange={setSelectedTrade}
            isClearable
            placeholder="Select trade"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Document Category <span className="text-red-600">*</span>
          </label>
          <Select
            options={categoryOptions}
            value={selectedCategory}
            onChange={(option) => handleCategoryChange(option)}
            isClearable={!isSelectedPreConstruction}
            isDisabled={isSelectedPreConstruction}
            placeholder="Select category"
          />
          {isSelectedPreConstruction ? (
            <p className="text-xs text-gray-500 mt-1">
              Pre-construction trade — documents from upload #
              {selectedTrade?.trade?.keyInfo?.preConstructionUploadNumber ||
                "7000+"}{" "}
              are shown below.
            </p>
          ) : null}
        </div>
      </div>

      {selectedTrade && !isAgent && (
        <div className="mb-6 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3">
          <p className="text-sm font-semibold text-emerald-900 mb-2">
            Signed Trade Record Sheets
          </p>
          {trsLoading ? (
            <p className="text-sm text-emerald-800">Loading signed TRS…</p>
          ) : (
            <ul className="divide-y divide-emerald-100 bg-white rounded-md border border-emerald-100">
              {[
                { kind: "open", label: "Open TRS (signed)" },
                { kind: "closed", label: "Closed TRS (signed)" },
              ].map(({ kind, label }) => {
                const slot = trsSignatures?.[kind] || {};
                const ready =
                  slot.status === "signed" || Boolean(slot.hasSignedFile);
                return (
                  <li
                    key={kind}
                    className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                  >
                    <span className="text-sm text-gray-800">
                      {label}
                      <span className="ml-2 text-xs font-medium text-emerald-800">
                        {ready
                          ? "Ready"
                          : slot.status === "awaiting_signature"
                            ? "Awaiting signature"
                            : "Not available"}
                      </span>
                    </span>
                    <div className="flex items-center gap-3 shrink-0">
                      <button
                        type="button"
                        className="text-sm font-medium text-blue-900 hover:underline disabled:text-gray-400"
                        disabled={!ready || trsActionId === `view-${kind}`}
                        onClick={() => handleViewSignedTrs(kind)}
                      >
                        {trsActionId === `view-${kind}` ? "Opening..." : "View"}
                      </button>
                      <button
                        type="button"
                        className="text-sm text-gray-600 hover:underline disabled:text-gray-400"
                        disabled={!ready || trsActionId === `download-${kind}`}
                        onClick={() => handleDownloadSignedTrs(kind)}
                      >
                        {trsActionId === `download-${kind}`
                          ? "Downloading..."
                          : "Download"}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {selectedTrade && selectedCategory && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-lg font-semibold text-blue-900">
              {selectedCategory.label}
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {canUploadPreConstruction && isSelectedPreConstruction && (
                <>
                  <input
                    ref={preConFileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,application/pdf,image/*"
                    className="hidden"
                    onChange={(e) => {
                      const selected = Array.from(e.target.files || []);
                      e.target.value = "";
                      if (selected.length) handlePreConstructionTradeUpload(selected);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        !selectedTrade?.trade?.keyInfo
                          ?.preConstructionUploadNumber
                      ) {
                        toast.error(
                          "Link a Pre Construction Upload number on Trade Info first, or create one under Pre Construction Uploads.",
                        );
                        setPreConstructionUploadsOpen(true);
                        return;
                      }
                      preConFileInputRef.current?.click();
                    }}
                    disabled={saving}
                    title="Select one or more files (Ctrl/Cmd+click or Shift+click)"
                    className="px-4 py-2 bg-blue-900 text-white rounded-md text-sm hover:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {saving ? "Uploading…" : "Upload files"}
                  </button>
                </>
              )}
              {canManageFiles && !isSelectedPreConstruction && (
                <>
                  <div className="min-w-[220px] w-full sm:w-72">
                    <Select
                      options={documentOptions}
                      value={selectedDocument}
                      onChange={setSelectedDocument}
                      isClearable
                      placeholder="Select document to upload"
                    />
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,application/pdf,image/*"
                    className="hidden"
                    onChange={(e) => {
                      const selected = Array.from(e.target.files || []);
                      e.target.value = "";
                      if (selected.length) handleUpload(selected);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!selectedDocument?.value) {
                        toast.error("Select the document you are uploading first.");
                        return;
                      }
                      fileInputRef.current?.click();
                    }}
                    disabled={saving || !selectedDocument?.value}
                    className="px-4 py-2 bg-blue-900 text-white rounded-md text-sm hover:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {saving ? "Checking form..." : "Upload"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setRevisedDocument(null);
                      setRevisedModalOpen(true);
                    }}
                    disabled={saving || revisedUploading}
                    className="px-4 py-2 bg-white text-blue-900 border border-blue-900 rounded-md text-sm hover:bg-blue-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed"
                  >
                    Revised Documents
                  </button>
                  {pendingFiles.length > 0 ? (
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-2 bg-blue-900 text-white rounded-md text-sm hover:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>
                  ) : null}
                </>
              )}
              {canRequestMissing && !isSelectedPreConstruction && (
                <button
                  type="button"
                  onClick={openRequestModal}
                  disabled={sending}
                  className="px-4 py-2 bg-blue-900 text-white rounded-md text-sm hover:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Email agent for missing documents ({missingCount})
                </button>
              )}
            </div>
          </div>

          <div className="mb-5 rounded-md border border-blue-200 bg-blue-50 px-4 py-3">
            <p className="text-sm font-semibold text-blue-900 mb-2">
              Uploaded files ({displayedFiles.length})
              {isSelectedPreConstruction &&
              selectedTrade?.trade?.keyInfo?.preConstructionUploadNumber
                ? ` · #${selectedTrade.trade.keyInfo.preConstructionUploadNumber}`
                : ""}
            </p>
            {displayedFiles.length === 0 ? (
              <p className="text-sm text-blue-800">
                {isSelectedPreConstruction
                  ? canUploadPreConstruction
                    ? "No pre-construction documents yet. Click Upload above, or use Pre Construction Uploads (7000+) to add files."
                    : "No pre-construction documents linked yet. An agent or Super User can upload under Pre Construction Uploads (7000+)."
                  : "No files uploaded yet. Select a document, then click Upload."}
              </p>
            ) : (
              <ul className="divide-y divide-blue-100 bg-white rounded-md border border-blue-100">
                {displayedFiles.map((file) => (
                  <li
                    key={file.fileId}
                    className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 ${
                      file.isRevision ? "bg-amber-50/80" : ""
                    }`}
                  >
                    <span className="text-sm text-gray-800 break-all flex flex-wrap items-center gap-2">
                      <span>{file.originalName || "Uploaded file"}</span>
                      {file.itemLabel ? (
                        <span className="text-xs font-medium text-blue-800">
                          {file.itemLabel.replace(/\.$/, "")}
                        </span>
                      ) : null}
                      {file.isRevision ? (
                        <span
                          className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900 ring-1 ring-amber-300"
                          title="This is a revised copy. The original file is still on file."
                        >
                          ★ Revised
                        </span>
                      ) : null}
                      {file.pending ? (
                        <span className="text-xs font-medium text-amber-700">
                          Not saved
                        </span>
                      ) : null}
                    </span>
                    <div className="flex items-center gap-3 shrink-0">
                      <button
                        type="button"
                        className="text-sm font-medium text-blue-900 hover:underline disabled:text-gray-400"
                        disabled={openingId === file.fileId}
                        onClick={() => handleView(file)}
                      >
                        {openingId === file.fileId ? "Opening..." : "View"}
                      </button>
                      <button
                        type="button"
                        className="text-sm text-gray-600 hover:underline disabled:text-gray-400"
                        disabled={openingId === file.fileId}
                        onClick={() => handleDownload(file)}
                      >
                        Download
                      </button>
                      {canRemoveFile(file) && (
                        <button
                          type="button"
                          className="text-sm text-red-700 hover:underline disabled:text-gray-400"
                          disabled={removingId === file.fileId || saving}
                          onClick={() => handleRemove(file)}
                        >
                          {removingId === file.fileId ? "Removing..." : "Remove"}
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {isSelectedPreConstruction ? null : loading ? (
            <p className="text-sm text-gray-500">Loading checklist...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-3">
              {columns.map((col, colIdx) => (
                <div key={colIdx} className="space-y-3">
                  {col.map((item) => (
                    <div
                      key={item.id}
                      className={`flex items-start gap-2 p-1.5 rounded-md cursor-pointer ${
                        selectedDocument?.value === item.id
                          ? "bg-blue-50 ring-1 ring-blue-300"
                          : "hover:bg-gray-50"
                      }`}
                      onClick={() =>
                        setSelectedDocument({
                          value: item.id,
                          label: String(item.label || "").replace(/\.$/, ""),
                          item,
                        })
                      }
                    >
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4"
                        checked={Boolean(item.checked)}
                        onChange={(e) => handleCheck(item, e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="min-w-0 flex-1">
                        <span className="text-sm text-gray-800 leading-snug">
                          {item.label}
                        </span>
                        {item.isOther && (
                          <input
                            type="text"
                            value={item.otherText || ""}
                            onChange={(e) =>
                              updateItem({ ...item, otherText: e.target.value })
                            }
                            onBlur={(e) =>
                              handleOtherText(item, e.target.value)
                            }
                            onClick={(e) => e.stopPropagation()}
                            placeholder="Specify"
                            className="mt-1 block w-full text-xs rounded-md border-gray-300 shadow-sm"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <RequestMissingDocumentsModal
        open={requestModalOpen}
        items={missingItems}
        selectedIds={requestItemIds}
        sending={sending}
        categoryLabel={selectedCategory?.label}
        onToggle={(id) =>
          setRequestItemIds((prev) =>
            prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
          )
        }
        onSelectAll={() => setRequestItemIds(missingItems.map((item) => item.id))}
        onClearAll={() => setRequestItemIds([])}
        onCancel={closeRequestModal}
        onSend={handleRequestMissing}
      />

      {revisedModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Upload revised document
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Select which document you are revising, then upload the new file.
              The previous file stays on the trade.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Document <span className="text-red-600">*</span>
            </label>
            <Select
              options={revisedDocumentOptions}
              value={revisedDocument}
              onChange={setRevisedDocument}
              isClearable
              placeholder="Select document to revise"
              className="mb-4"
            />
            <input
              ref={revisedFileInputRef}
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,application/pdf,image/*"
              className="hidden"
              onChange={(e) => {
                const selected = Array.from(e.target.files || []);
                e.target.value = "";
                if (selected.length) handleRevisedUpload(selected);
              }}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (revisedUploading) return;
                  setRevisedModalOpen(false);
                  setRevisedDocument(null);
                }}
                disabled={revisedUploading}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!revisedDocument?.value) {
                    toast.error("Select the document you are revising first.");
                    return;
                  }
                  revisedFileInputRef.current?.click();
                }}
                disabled={revisedUploading || !revisedDocument?.value}
                className="px-4 py-2 bg-blue-900 text-white rounded-md text-sm hover:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {revisedUploading ? "Uploading..." : "Choose file & upload"}
              </button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[90vh] flex flex-col">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
              <h4 className="font-semibold text-blue-900 truncate">
                {preview.name}
              </h4>
              <button
                type="button"
                onClick={closePreview}
                className="px-3 py-1.5 text-sm bg-blue-900 text-white rounded-md hover:bg-blue-800"
              >
                Close
              </button>
            </div>
            <div className="flex-1 min-h-0 bg-gray-100">
              {String(preview.mimeType || "").startsWith("image/") ? (
                <img
                  src={preview.url}
                  alt={preview.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <iframe
                  title={preview.name}
                  src={preview.url}
                  className="w-full h-full border-0 bg-white"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeDocumentsSection;
